import React, { useState, useRef } from 'react';
import { Upload, X, ImageIcon } from 'lucide-react';
import { Button } from './ui/button';
import { cn } from '../lib/utils';

interface DropAreaProps {
  imageUrl?: string;
  enforceWidth: boolean;
  enforceHeight: boolean;
  requiredHeight: number;
  requiredWidth: number;
  multiple: boolean;
  accept: string;
  label: string;
  onFileUpload: (file: File) => void;
  onMultipleFilesUpload?: (files: File[]) => void;
  onFileDelete: () => void;
  sizeScale: number;
  padding: string;
}

export const DropArea: React.FC<DropAreaProps> = ({
  imageUrl,
  enforceWidth,
  enforceHeight,
  requiredHeight,
  requiredWidth,
  multiple,
  accept,
  label,
  onFileUpload,
  onMultipleFilesUpload,
  onFileDelete,
  sizeScale,
  padding,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      if (multiple && onMultipleFilesUpload) {
        onMultipleFilesUpload(files);
      } else if (files[0]) {
        onFileUpload(files[0]);
      }
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      if (multiple && onMultipleFilesUpload) {
        onMultipleFilesUpload(Array.from(files));
      } else if (files[0]) {
        onFileUpload(files[0]);
      }
    }
  };

  const handleClick = () => {
    if (!imageUrl) {
      fileInputRef.current?.click();
    }
  };

  const scaledWidth = requiredWidth / sizeScale;
  const scaledHeight = requiredHeight / sizeScale;

  return (
    <div
      className={cn(
        "relative border-2 border-dashed rounded-lg transition-all duration-200 group",
        isDragOver 
          ? "border-primary bg-primary/5 scale-[1.02]" 
          : "border-border hover:border-primary/50 hover:bg-accent/20",
        !imageUrl && "cursor-pointer"
      )}
      style={{ 
        width: scaledWidth, 
        height: scaledHeight,
        minHeight: scaledHeight
      }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handleClick}
    >
      {imageUrl ? (
        <div className="relative w-full h-full">
          <img
            src={imageUrl}
            alt="Uploaded thumbnail"
            className="w-full h-full object-cover rounded-md"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-200 rounded-md" />
          <Button
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
            onClick={(e) => {
              e.stopPropagation();
              onFileDelete();
            }}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-full text-center p-6">
          <div className={cn(
            "rounded-full p-3 mb-4 transition-colors duration-200",
            isDragOver ? "bg-primary/10" : "bg-secondary"
          )}>
            <Upload className={cn(
              "h-6 w-6 transition-colors duration-200",
              isDragOver ? "text-primary" : "text-muted-foreground"
            )} />
          </div>
          <p className={cn(
            "text-sm font-medium mb-2 transition-colors duration-200",
            isDragOver ? "text-primary" : "text-foreground"
          )}>
            Drop your image here
          </p>
          <p className="text-xs text-muted-foreground whitespace-pre-line leading-relaxed">
            {label}
          </p>
        </div>
      )}
      
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={handleFileSelect}
      />
    </div>
  );
};